import Component from '@glimmer/component'
import { service } from '@ember/service'
import type ReportingService from 'district-ui-client/services/reporting'
import { t, type IntlService } from 'ember-intl'
import { formatDate } from 'district-ui-client/services/reporting'
import { TooltipInfo } from 'district-ui-client/components/tooltip'
import type GradeSetsService from 'district-ui-client/services/grade-sets'
import type MxLessonLevelDomainService from 'district-ui-client/services/mx-lesson-level-domain'
import { Product } from 'district-ui-client/domain/product'

interface Signature {
  Element: HTMLDivElement
}

export class ReportingScopeHeader extends Component<Signature> {
  @service reporting!: ReportingService

  @service intl!: IntlService

  @service gradeSets!: GradeSetsService

  @service mxLessonLevelDomain!: MxLessonLevelDomainService

  get isMathletics() {
    return this.reporting.product === Product.MX
  }

  get uiScopeFormatted() {
    const { uiScope } = this.reporting
    if (uiScope?.scope === 'school') return this.intl.t('reporting.scopes.someSchools', { count: 1 })
    if (uiScope?.scope === 'district' && uiScope.subScopes.length) {
      return this.intl.t('reporting.scopes.someSchools', { count: uiScope.subScopes.length })
    }
    return this.intl.t('reporting.scopes.allSchools')
  }

  get gradeScopeFormatted() {
    const { studentGrade } = this.reporting
    if (studentGrade === 'all') return this.intl.t('reporting.scopes.allGrades')
    return this.gradeSets.findByPosition(parseInt(studentGrade, 10))?.fullName ?? ''
  }

  get contentLevelScopeFormatted() {
    const { contentLevel } = this.reporting
    if (contentLevel === 'all') return this.intl.t('reporting.scopes.allContentLevels')
    const gradeName = (this.gradeSets.findByPosition(parseInt(contentLevel, 10))?.fullName ?? '').toLowerCase()
    return this.intl.t('reporting.scopes.contentLevel', { gradeName })
  }

  get gradeDomainScopeFormatted() {
    const { selectedLessonLevelDomainIds } = this.mxLessonLevelDomain
    const allCount = this.mxLessonLevelDomain.records.length
    const count = selectedLessonLevelDomainIds.length
    switch (true) {
      case count === 0:
      case count === allCount:
        return this.intl.t('reporting.scopes.gradeAndDomain.allSelected')
      case count === 1:
        return selectedLessonLevelDomainIds[0]
      default:
        return this.intl.t('reporting.scopes.gradeAndDomain.someSelected', { count })
    }
  }

  get periodFormatted() {
    const { period, namedRange } = this.reporting
    if (namedRange) return namedRange.label

    return `${formatDate(period.start)} - ${formatDate(period.end)}`
  }

  <template>
    <div data-test-scope-header class="flex flex-wrap items-center gap-4 text-xl text-neutral-400" ...attributes>
      <span class="font-medium">{{this.uiScopeFormatted}}</span>
      <span class="h-6 border-x border-neutral-75" />
      <span class="font-medium">{{this.gradeScopeFormatted}}</span>
      <span class="h-6 border-x border-neutral-75" />
      <span class="font-medium">
        {{#if this.isMathletics}}
          {{this.gradeDomainScopeFormatted}}
        {{else}}
          {{this.contentLevelScopeFormatted}}
        {{/if}}
      </span>
      <span class="h-6 border-x border-neutral-75" />
      <span class="font-medium">{{this.periodFormatted}}</span>
      <TooltipInfo class="text-base" @text={{t "reporting.scopeHeaderTooltip"}} />
      <div id="export-button-wormhole" class="ml-auto" />
    </div>
  </template>
}
